import { logger } from '@/lib/utils/logger';
import { createClient } from "@supabase/supabase-js";
import { storageBackup } from './storage-backup';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

if ([supabaseUrl, supabaseAnonKey].some((value) => !value)) { // keep inside `.some()` — an unconditional top-level throw makes the bundler drop re-exported bindings (#921)
  throw new Error("Missing Supabase environment variables");
}

// Validate that we're not using insecure URLs in production
if (import.meta.env.PROD && (supabaseUrl.startsWith('http://') || supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1'))) {
  logger.error('Insecure Supabase URL detected in production:', supabaseUrl);
  throw new Error("Insecure Supabase URL detected in production environment");
}

const _isServer = typeof window === 'undefined';
export const SUPABASE_AUTH_STORAGE_KEY = 'sb-auth';

export function isSupabaseAuthStorageKey(key: string | null | undefined): key is string {
  return key === SUPABASE_AUTH_STORAGE_KEY;
}

async function clearAuthStorageKey(key: string = SUPABASE_AUTH_STORAGE_KEY): Promise<void> {
  if (_isServer) {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch (error) {
    logger.error('Failed to clear auth token from localStorage:', error);
  }

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    logger.error('Failed to clear auth token from sessionStorage:', error);
  }

  try {
    await storageBackup.clear(key);
  } catch (error) {
    logger.error('Failed to clear auth token backups:', error);
  }
}

// Track failed refresh attempts to prevent cascades
const _failedRefreshCount = 0;
const _lastFailedRefreshTime = 0;
const _permanentlyBlockRefresh = false;

// Track 400 errors to prevent retry loops
let consecutive400Errors = 0;
let lastErrorTime = 0;

const customFetch = async (url: RequestInfo | URL, options?: RequestInit): Promise<Response> => {
  try {
    const response = await fetch(url, options);
    
    if (response.status === 400 && url.toString().includes('/auth/v1/token')) {
      const now = Date.now();
      
      if (now - lastErrorTime > 5000) {
        consecutive400Errors = 0;
      }
      
      consecutive400Errors++;
      lastErrorTime = now;
      
      logger.warn(`Token refresh failed with 400 (${consecutive400Errors} consecutive errors)`);
      
      if (consecutive400Errors >= 2) {
        logger.error('Multiple token refresh failures detected, clearing session to prevent loops');
        
        await clearAuthStorageKey();
        
        consecutive400Errors = 0;
        
        return new Response(JSON.stringify({ error: 'Invalid refresh token' }), {
          status: 400,
          headers: { 'Content-Type': 'application/json' }
        });
      }
    } else if (response.ok) {
      consecutive400Errors = 0;
    }
    
    return response;
  } catch (error) {
    logger.error('Fetch error:', error);
    throw error;
  }
};

const storage = {
  getItem: async (key: string): Promise<string | null> => {
    try {
      const value = localStorage.getItem(key);
      if (import.meta.env.DEV) {
        logger.debug(`Supabase storage getItem(${key}):`, value ? 'found' : 'null');
      }
      
      if (!value && isSupabaseAuthStorageKey(key)) {
        if (import.meta.env.DEV) logger.debug('Session is null, attempting recovery...');
        
        const recoveredValue = await storageBackup.restore(key);
        if (recoveredValue) {
          try {
            JSON.parse(recoveredValue);
            localStorage.setItem(key, recoveredValue);
            if (import.meta.env.DEV) logger.info('Session restored to localStorage from backup');
            return recoveredValue;
          } catch (e) {
            logger.error('Failed to restore session - invalid JSON:', e);
          }
        } else {
          if (import.meta.env.DEV) logger.debug('No backup found in any storage layer');
          
          if (key === SUPABASE_AUTH_STORAGE_KEY && import.meta.env.DEV) {
            logger.debug('Auth token missing - possible fresh start or cleared storage');
          }
        }
      }
      
      // Temporarily disable aggressive validation - just log what we find
      if (value && isSupabaseAuthStorageKey(key) && import.meta.env.DEV) {
        try {
          const parsed = JSON.parse(value);
          logger.debug(`Session data structure for key ${key}:`, Object.keys(parsed));
          // DISABLED: Don't remove sessions even if they seem invalid
          // Let Supabase handle session validation
        } catch (e) {
          logger.warn(`Corrupted session data in localStorage for key ${key}`, e);
          // DON'T remove the session - let Supabase handle it
        }
      }
      return value;
    } catch (error) {
      logger.error('Error reading from localStorage:', error);
      
      // Try multi-level recovery as fallback
      try {
        const backupValue = await storageBackup.restore(key);
        if (backupValue && isSupabaseAuthStorageKey(key)) {
          if (import.meta.env.DEV) logger.info('Using backup due to localStorage error');
          return backupValue;
        }
      } catch (e) {
        logger.error('Backup recovery also failed:', e);
      }
      
      return null;
    }
  },
  setItem: async (key: string, value: string): Promise<void> => {
    try {
      if (import.meta.env.DEV) {
        logger.debug(`Supabase storage setItem(${key}):`, value ? 'setting value' : 'null value');
      }
      
      // Temporarily disable validation on setItem too
      if (isSupabaseAuthStorageKey(key) && value) {
        try {
          const parsed = JSON.parse(value);
          if (import.meta.env.DEV) logger.debug(`Storing session data with keys:`, Object.keys(parsed));
          
          await storageBackup.backup(key, value);
        } catch (e) {
          logger.warn(`Invalid JSON for session data for key ${key}`, e);
          return;
        }
      }
      localStorage.setItem(key, value);
      if (import.meta.env.DEV) logger.debug(`Supabase storage setItem(${key}): success`);
    } catch (error) {
      logger.error('Error writing to localStorage:', error);
      
      if (isSupabaseAuthStorageKey(key)) {
        try {
          await storageBackup.backup(key, value);
          if (import.meta.env.DEV) logger.info('Session saved to backup system as fallback');
        } catch (e) {
          logger.error('Backup system also failed:', e);
        }
      }
    }
  },
  removeItem: async (key: string): Promise<void> => {
    try {
      if (import.meta.env.DEV) logger.debug(`Supabase storage removeItem(${key})`);
      if (isSupabaseAuthStorageKey(key) && import.meta.env.DEV) {
        logger.debug('Session removal stack trace:', new Error().stack);
      }
      localStorage.removeItem(key);
      if (isSupabaseAuthStorageKey(key)) {
        await storageBackup.clear(key);
      }
    } catch (error) {
      logger.error('Error removing from localStorage:', error);
    }
  }
};

// NOTE: Temporarily use permissive typing while database-generated types are reconciled.
export const supabase = createClient<any>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Add a storage key prefix to avoid conflicts
    storageKey: SUPABASE_AUTH_STORAGE_KEY,
    storage
  },
  global: {
    headers: {
      "X-Client-Info": "rendezvous-web",
    },
    fetch: customFetch,
  },
  db: {
    schema: 'public'
  }
});

// This prevents duplicate listeners and race conditions

if (import.meta.env.DEV) {
  supabase.auth.onAuthStateChange((event, session) => {
    logger.debug('Supabase auth event:', event, session?.user?.email);
  });
}
